import { cleanFloat } from "@/lib/tool-kit/grades/rounding";

/**
 * Letter-grade scales for the letter grade calculator, the easy grader and the rubric
 * score calculator.
 *
 * There is no national US grading scale: every school, and often every syllabus, sets
 * its own cutoffs. These are presets for the common conventions, and every tool that
 * uses them also lets the reader choose another.
 *
 * Sources (checked 2026):
 * - Plus/minus grade points (A 4.0, A− 3.7, B+ 3.3 … D− 0.7, F 0.0) are the common US
 *   college values — e.g. Pepperdine University Registrar, "Definition of Grade Points",
 *   https://www.pepperdine.edu/registrar/grades/pointdefinition.htm. Some colleges use
 *   thirds instead (3.67, 3.33) and a few still count A+ as 4.3.
 * - The 93/90/87 … percentage cutoffs are the same plus/minus bands the site's weighted
 *   grade and test average calculators use.
 * - The 10-point A–F scale (A 90–100 = 4, B 80–89 = 3, C 70–79 = 2, D 60–69 = 1,
 *   F 0–59 = 0) is the one Florida law sets for public grades 6–12: Fla. Stat.
 *   § 1003.437, https://www.flsenate.gov/Laws/Statutes/2023/1003.437.
 */

export interface LetterBand {
  /** The letter as printed: "A", "B+", "F". */
  letter: string;
  /** The lowest percentage that earns this letter, inclusive. */
  min: number;
  /** Grade points on the 4.0 scale. */
  points: number;
}

export type LetterScalePreset =
  | "plus-minus"
  | "plus-minus-a-plus"
  | "ten-point";

/** Unicode minus in the letters, so "A−" reads as a grade rather than a hyphenation. */
const PLUS_MINUS: readonly LetterBand[] = [
  { letter: "A", min: 93, points: 4 },
  { letter: "A−", min: 90, points: 3.7 },
  { letter: "B+", min: 87, points: 3.3 },
  { letter: "B", min: 83, points: 3 },
  { letter: "B−", min: 80, points: 2.7 },
  { letter: "C+", min: 77, points: 2.3 },
  { letter: "C", min: 73, points: 2 },
  { letter: "C−", min: 70, points: 1.7 },
  { letter: "D+", min: 67, points: 1.3 },
  { letter: "D", min: 63, points: 1 },
  { letter: "D−", min: 60, points: 0.7 },
  { letter: "F", min: 0, points: 0 },
];

export const LETTER_SCALE_PRESETS: Record<
  LetterScalePreset,
  readonly LetterBand[]
> = {
  "plus-minus": PLUS_MINUS,
  "plus-minus-a-plus": [{ letter: "A+", min: 97, points: 4 }, ...PLUS_MINUS],
  "ten-point": [
    { letter: "A", min: 90, points: 4 },
    { letter: "B", min: 80, points: 3 },
    { letter: "C", min: 70, points: 2 },
    { letter: "D", min: 60, points: 1 },
    { letter: "F", min: 0, points: 0 },
  ],
};

/** Highest cutoff first — the order a lookup walks. */
export function sortBands(bands: readonly LetterBand[]): LetterBand[] {
  return [...bands].sort((a, b) => b.min - a.min);
}

/**
 * The band a percentage falls in, or null when it is below every cutoff (only possible
 * on a custom scale with no band starting at 0).
 */
export function letterFor(
  percent: number,
  bands: readonly LetterBand[]
): LetterBand | null {
  const value = cleanFloat(percent);
  for (const band of sortBands(bands)) {
    if (value >= band.min) {
      return band;
    }
  }
  return null;
}

/** Points earned as a percentage, computed so 179 / 200 is exactly 89.5. */
export function percentFromPoints(earned: number, possible: number): number {
  return cleanFloat((earned * 100) / possible);
}

/**
 * The lowest unrounded percentage that still earns a band.
 *
 * With rounding to the nearest whole percent, 92.5 becomes 93, so an A that starts at
 * 93 really starts at 92.5. A band that starts at 0 stays at 0.
 */
export function effectiveMin(min: number, roundsToWhole: boolean): number {
  if (!roundsToWhole || min <= 0) {
    return min;
  }
  return min - 0.5;
}

/** The fewest points out of `possible` that earn a band. */
export function minPointsFor(
  min: number,
  possible: number,
  roundsToWhole: boolean
): number {
  return cleanFloat((effectiveMin(min, roundsToWhole) * possible) / 100);
}

/** A custom row as typed: kept only when its cutoff and points are real numbers. */
export function validBands(
  rows: readonly { letter: string; min: number | null; points: number | null }[]
): LetterBand[] {
  const bands: LetterBand[] = [];
  for (const row of rows) {
    const letter = row.letter.trim();
    if (letter === "" || row.min === null || row.points === null) {
      continue;
    }
    if (row.min < 0 || row.min > 100 || row.points < 0) {
      continue;
    }
    bands.push({ letter, min: row.min, points: row.points });
  }
  return sortBands(bands);
}
